import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, filter } from 'rxjs/operators';
import { BuilderNode } from '../interfaces/builder-screen.models';
import { BuilderScreenBetaService } from '../services/builder-screen.service';

@Component({
  selector: 'app-property-control',
  templateUrl: './property-control.component.html',
  styleUrls: ['./property-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone:false
})
export class PropertyControlComponent implements OnInit, OnDestroy {
  @Input() property!: string;
  @Input() component!: BuilderNode | null;
  @Input() displayName!: string;
  @Input() description?: string;
  @Input() compact = false;

  control = new FormControl();
  propertyType: 'text' | 'number' | 'boolean' | 'select' | 'color' | 'textarea' = 'text';
  selectOptions: Array<{ label: string; value: any }> = [];

  // CORREÇÃO CRÍTICA: Debouncing do seletor de cores e prevenção de condições de corrida
  private destroy$ = new Subject<void>();
  private colorUpdateSubject = new Subject<string>();
  private isUpdatingColor = false;

  constructor(
    private builderService: BuilderScreenBetaService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    if (!this.component) return;

    this.determinePropertyType();
    this.initializeControl();
    this.setupValueChanges();

    // CORREÇÃO CRÍTICA: Configura atualizações de cor com debounce para propriedades de cor
    if (this.propertyType === 'color') {
      this.setupDebouncedColorUpdates();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private determinePropertyType() {
    // Determina tipo de input baseado no nome da propriedade
    switch (this.property) {
      case 'required':
      case 'disabled':
      case 'isDisabled':
      case 'loading':
      case 'readonly':
      case 'gridVisible':
      case 'previewMode':
        this.propertyType = 'boolean';
        break;
        
      case 'min':
      case 'max':
      case 'step':
      case 'precision':
      case 'minLength':
      case 'maxLength':
      case 'col':
      case 'row':
      case 'colSpan':
      case 'zoom':
        this.propertyType = 'number';
        break;
        
      case 'backgroundColor':
      case 'textColor':
      case 'borderColor':
      case 'hoverColor':
      case 'activeTabColor':
      case 'headerBackgroundColor':
      case 'rowBackgroundColor':
      case 'alternateRowColor':
        this.propertyType = 'color';
        break;
        
      case 'buttonType':
        this.propertyType = 'select';
        this.selectOptions = [
          { label: 'Default', value: 'default' },
          { label: 'Primary', value: 'primary' },
          { label: 'Dashed', value: 'dashed' },
          { label: 'Text', value: 'text' },
          { label: 'Link', value: 'link' },
          { label: 'Danger', value: 'danger' },
        ];
        break;
        
      case 'size':
        this.propertyType = 'select';
        this.selectOptions = [
          { label: 'Small', value: 'small' },
          { label: 'Default', value: 'default' },
          { label: 'Large', value: 'large' },
        ];
        break;
        
      case 'actionType':
        this.propertyType = 'select';
        this.selectOptions = [
          { label: 'Save', value: 'save' },
          { label: 'Cancel', value: 'cancel' },
          { label: 'Custom', value: 'custom' },
        ];
        break;
        
      case 'panel':
        this.propertyType = 'select';
        this.selectOptions = [
          { label: 'General', value: 'general' },
          { label: 'Advanced', value: 'advanced' },
          { label: 'Custom', value: 'custom' },
        ];
        break;
        
      case 'placeholder':
      case 'pattern':
        if (this.property === 'placeholder' && this.component?.type === 'textarea') {
          this.propertyType = 'textarea';
        } else {
          this.propertyType = 'text';
        }
        break;
        
      default:
        this.propertyType = 'text';
        break;
    }
  }

  private initializeControl() {
    if (!this.component) return;

    const currentValue = this.getPropertyValue();
    this.control.setValue(currentValue);
  }

  private getPropertyValue(): any {
    if (!this.component) return null;

    const props = this.component.props as any;
    return props?.[this.property] ?? this.getDefaultValue();
  }

  private getDefaultValue(): any {
    switch (this.propertyType) {
      case 'boolean':
        return false;
      case 'number':
        return this.property === 'colSpan' ? 24 : 0;
      case 'color':
        return '#ffffff';
      case 'select':
        return this.selectOptions[0]?.value || '';
      default:
        return '';
    }
  }

  private setupValueChanges() {
    // CORREÇÃO CRÍTICA: Tratamento diferente para propriedades de cor vs não-cor
    if (this.propertyType === 'color') {
      // Para propriedades de cor, usa atualizações com debounce através do colorUpdateSubject
      this.control.valueChanges
        .pipe(
          takeUntil(this.destroy$),
          filter(() => !this.isUpdatingColor) // Previne loops de feedback
        )
        .subscribe(value => {
          if (this.component && value) {
            this.colorUpdateSubject.next(value);
          }
        });
    } else {
      // Para propriedades não-cor, usa atualizações imediatas
      this.control.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          if (this.component) {
            this.updateComponentProperty(value);
          }
        });
    }
  }

  /**
   * CRITICAL FIX: Setup debounced color updates to prevent UI freezing
   */
  private setupDebouncedColorUpdates(): void {
    console.log(`🎨 Setting up debounced color updates for property: ${this.property}`);
    
    // Debounce de mudanças de cor para prevenir travamento da UI
    this.colorUpdateSubject
      .pipe(
        debounceTime(150), // Aguarda 150ms após última mudança
        distinctUntilChanged(), // Emite apenas se o valor realmente mudou
        takeUntil(this.destroy$)
      )
      .subscribe((color) => {
        this.updateColorValue(color);
      });
  }

  private updateComponentProperty(value: any) {
    if (!this.component) return;

    const updatedProps = {
      ...this.component.props,
      [this.property]: value,
    };

    const updatedComponent: BuilderNode = {
      ...this.component,
      props: updatedProps,
    };

    this.builderService.updateNode(updatedComponent);
  }

  /**
   * CRITICAL FIX: Safe color value update with race condition prevention
   */
  private updateColorValue(color: string): void {
    // Previne condições de corrida durante atualizações de cor
    if (this.isUpdatingColor) {
      console.warn(`⚠️ Color update already in progress for ${this.property}, skipping`);
      return;
    }

    this.isUpdatingColor = true;

    try {
      console.log(`🎨 Updating color property ${this.property} to: ${color}`);
      
      if (!this.component) {
        console.warn(`⚠️ No component available for color update`);
        return;
      }

      // Atualiza a propriedade do componente
      const updatedProps = {
        ...this.component.props,
        [this.property]: color,
      };

      const updatedComponent: BuilderNode = {
        ...this.component,
        props: updatedProps,
      };

      this.builderService.updateNode(updatedComponent);

      // Dispara detecção de mudança para estratégia OnPush
      this.cdr.markForCheck();
      
      console.log(`✅ Color property ${this.property} updated successfully`);
    } catch (error) {
      console.error(`❌ Error updating color property ${this.property}:`, error);
    } finally {
      // Sempre libera o bloqueio após um pequeno delay
      setTimeout(() => {
        this.isUpdatingColor = false;
      }, 50);
    }
  }

  getInputPlaceholder(): string {
    const placeholders: Record<string, string> = {
      'entityName': 'e.g. Mktpproducts',
      'fieldName': 'e.g. CODEPRODUCT',
      'label': 'Enter field label',
      'placeholder': 'Enter placeholder text',
      'text': 'Enter button text',
      'pattern': 'Enter regex pattern',
      'icon': 'e.g. user, home, setting',
      'title': 'Enter section title',
    };

    return placeholders[this.property] || 'Enter value';
  }

  getHelpText(): string {
    if (this.description) {
      return this.description;
    }

    const helpTexts: Record<string, string> = {
      'colSpan': 'Grid columns to span (1-24)',
      'min': 'Minimum allowed value',
      'max': 'Maximum allowed value',
      'step': 'Increment step for number input',
      'precision': 'Number of decimal places',
      'minLength': 'Minimum character length',
      'maxLength': 'Maximum character length',
      'pattern': 'Regular expression for validation',
    };

    return helpTexts[this.property] || '';
  }

  getNumberMin(): number {
    switch (this.property) {
      case 'colSpan':
        return 1;
      case 'col':
      case 'row':
        return 0;
      case 'minLength':
      case 'maxLength':
        return 0;
      default:
        return -Infinity;
    }
  }

  getNumberMax(): number {
    switch (this.property) {
      case 'colSpan':
        return 24;
      default:
        return Infinity;
    }
  }

  getNumberStep(): number {
    switch (this.property) {
      case 'step':
      case 'precision':
        return 0.1;
      default:
        return 1;
    }
  }

  getValidationMessage(): string {
    if (this.control.errors) {
      if (this.control.errors['required']) {
        return 'This field is required';
      }
      if (this.control.errors['min']) {
        return `Minimum value is ${this.control.errors['min'].min}`;
      }
      if (this.control.errors['max']) {
        return `Maximum value is ${this.control.errors['max'].max}`;
      }
      if (this.control.errors['pattern']) {
        return 'Invalid format';
      }
    }
    return 'Invalid value';
  }
}
